import PropTypes from "prop-types";
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from "@/components/ui/empty";
import { Spinner } from "@/components/ui/spinner";
import ACL_RELATIONSHIPS from "acl-relationships";
import {
  useCustomFieldConfigs,
  useCustomFieldGroups,
} from "components/BaseForm";
import DetailHeaderBar from "components/DetailHeaderBar";
import { DetailBreadcrumb } from "components/sidebar/DetailBreadcrumb";
import useAuthorized from "hooks/useAuthorized";
import { useEntity } from "hooks/useEntity";
import usePageTitle from "hooks/usePageTitle";
import EntityForm from "routes/entity/form/EntityForm";
import {
  BREADCRUMB_LABELS,
  MODEL_TYPE,
  PAGE_TITLES,
} from "routes/entity/form/constants";
import { FORM_MODES } from "routes/entity/form/defaults";
import { entityPaths } from "routes/entity/paths";

const PageMessage = ({ children }) => (
  <Empty>
    <EmptyHeader>
      <EmptyDescription>{children}</EmptyDescription>
    </EmptyHeader>
    <EmptyContent>
      <Link to={entityPaths.list}>Back to Entities</Link>
    </EmptyContent>
  </Empty>
);

PageMessage.propTypes = { children: PropTypes.node };

/**
 * The page chrome: the same header bar as the record page above a scrolling
 * body. The breadcrumb links back to the list and, on edit, to the entity.
 */
const PageShell = ({ mode, entityId, entityName, children }) => (
  <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:font-sans tw:text-(--color-text-primary)">
    <DetailHeaderBar
      breadcrumb={
        <DetailBreadcrumb
          parentTo={entityPaths.list}
          parentLabel="All Entities"
          trail={
            entityId
              ? [
                  {
                    to: entityPaths.detail(entityId),
                    label: entityName ?? `Entity (${entityId})`,
                  },
                ]
              : []
          }
          current={BREADCRUMB_LABELS[mode]}
        />
      }
      title={PAGE_TITLES[mode]}
    />
    {/* The gutter stays reserved so the form does not shift sideways as the
        scrollbar comes and goes. */}
    <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:p-4 tw:scrollbar-gutter-stable">
      <div className="tw:mx-auto tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        {children}
      </div>
    </div>
  </div>
);

PageShell.propTypes = {
  mode: PropTypes.oneOf(Object.values(FORM_MODES)).isRequired,
  entityId: PropTypes.string,
  entityName: PropTypes.string,
  children: PropTypes.node,
};

// `from` carries the query string, so the list may arrive as "/entities?page=2".
const isListPath = path => path.split("?")[0] === entityPaths.list;

/** The full-page Entity create and edit form. */
const EntityFormPage = () => {
  // Only the edit route has an :id segment.
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const mode = id ? FORM_MODES.edit : FORM_MODES.create;
  const isEdit = mode === FORM_MODES.edit;
  const from = location.state?.from;
  const returnTo = from ?? entityPaths.list;
  // Opened from a record page, such as the entity itself or the Contact or
  // Case whose sidebar started the edit, a save returns there. From the
  // Entities list, or opened directly, it opens the saved entity.
  const isFromRecordPage = Boolean(from) && !isListPath(from);

  // The same permissions gate the create and edit actions.
  const canWrite = useAuthorized([
    isEdit ? ACL_RELATIONSHIPS.entity.edit : ACL_RELATIONSHIPS.entity.create,
    ACL_RELATIONSHIPS.user.read,
  ]);

  const {
    data: record,
    isSuccess: isRecordLoaded,
    isFetchedAfterMount,
    isError: isRecordError,
    error: recordError,
  } = useEntity(canWrite ? id : undefined);
  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously, and the form latches its defaults at the first render, so
  // mounting on it would save stale values over a newer edit.
  const isRecordReady = !id || (isRecordLoaded && isFetchedAfterMount);

  const configsQuery = useCustomFieldConfigs(MODEL_TYPE);
  const groupsQuery = useCustomFieldGroups(MODEL_TYPE);

  usePageTitle(PAGE_TITLES[mode]);

  const handleSaved = saved =>
    navigate(isFromRecordPage ? from : entityPaths.detail(saved.id));
  const handleCancel = () => navigate(returnTo);

  if (!canWrite) {
    return <Navigate to={entityPaths.list} replace />;
  }

  const shell = content => (
    <PageShell mode={mode} entityId={id} entityName={record?.name}>
      {content}
    </PageShell>
  );

  if (isRecordError) {
    const isNotFound = recordError?.response?.status === 404;
    return shell(
      <PageMessage>
        {isNotFound
          ? "This Entity does not exist."
          : "Could not load this Entity. Please try again."}
      </PageMessage>
    );
  }

  if (configsQuery.isError) {
    return shell(
      <PageMessage>Could not load the form. Please try again.</PageMessage>
    );
  }

  const isLoading =
    !isRecordReady || configsQuery.isPending || groupsQuery.isPending;

  if (isLoading) {
    return shell(
      <div className="tw:flex tw:justify-center tw:p-8">
        <Spinner />
      </div>
    );
  }

  return shell(
    <EntityForm
      mode={mode}
      record={record}
      customFieldConfigs={configsQuery.data ?? []}
      customFieldGroups={groupsQuery.data ?? []}
      onSaved={handleSaved}
      onCancel={handleCancel}
    />
  );
};

export default EntityFormPage;
